import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Scale } from "lucide-react";
import { DECISION_EXTRA_BRANCHES, decisionRoutedQuestion, decisionValueHandles } from "@proclaw/shared";
import { cn } from "@/lib/utils";
import {
  runNodeBadgeClasses,
  runNodeBadgeLabel,
  runNodeDotClasses,
  runNodeFrameClasses,
  runNodeGlowClasses,
} from "@/components/workflows/node-theme";

const EXTRA_LABELS: Record<(typeof DECISION_EXTRA_BRANCHES)[number], string> = {
  none: "None fits",
  low_confidence: "Unsure",
  unavailable: "Unavailable",
};

/**
 * Decision node: one source handle per option value of the routed question
 * (yes/no for a noul, a level index for a score), plus the three extra
 * outcomes. The handle set is computed by the same shared helper the
 * validator and runner use, so what the canvas draws is what the run follows.
 */
export function DecisionNode({ data, selected }: NodeProps) {
  const d = data as {
    label?: string;
    description?: string;
    questions?: Array<{ id?: string; type?: string }>;
    route?: { fallbackBranch?: string; minConfidence?: number };
    runStatus?: string;
    runSelected?: boolean;
  };
  const valueHandles = decisionValueHandles(d);
  const routed = decisionRoutedQuestion(d);
  const questionCount = Array.isArray(d.questions) ? d.questions.length : 0;
  const fallback = d.route?.fallbackBranch;
  const runFrame = runNodeFrameClasses(d.runStatus);
  const runDot = runNodeDotClasses(d.runStatus);
  const runGlow = runNodeGlowClasses(d.runStatus);
  const runBadge = runNodeBadgeLabel(d.runStatus);
  const runBadgeClasses = runNodeBadgeClasses(d.runStatus);

  const summary =
    valueHandles.length === 0
      ? "Add a question with options"
      : `${routed?.type === "noul" ? "Yes / no" : routed?.type === "score" ? `${valueHandles.length} levels` : `${valueHandles.length} options`}${
          questionCount > 1 ? ` · ${questionCount} questions` : ""
        }${typeof d.route?.minConfidence === "number" ? ` · min ${Math.round(d.route.minConfidence * 100)}%` : ""}`;

  return (
    <div
      className={cn(
        "relative w-[260px] rounded-xl border bg-surface shadow-sm transition-all duration-300",
        runFrame ?? (selected ? "border-sky-500 shadow-sky-500/20" : "border-border"),
        d.runSelected && "outline outline-2 outline-offset-4 outline-accent/70",
      )}
    >
      {runGlow && (
        <span className={cn("pointer-events-none absolute -inset-3 z-0 rounded-[1.35rem]", runGlow)} />
      )}
      {runDot && (
        <span className={cn("absolute right-2 top-2 z-10 h-2.5 w-2.5 rounded-full ring-2 ring-surface", runDot)} />
      )}
      {runBadge && runBadgeClasses && (
        <span className={cn("absolute -right-2 -top-3 z-20 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide", runBadgeClasses)}>
          {runBadge}
        </span>
      )}

      <div className="flex items-center gap-1.5 bg-sky-500/10 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-sky-600">
        <Scale size={11} />
        Decision
      </div>
      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-500/10 text-sky-600">
          <Scale size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-text-primary">
            {d.label || "Decision"}
          </p>
          <p className="mt-0.5 line-clamp-2 text-[11px] text-text-muted">
            {d.description || summary}
          </p>
        </div>
      </div>

      <Handle
        type="target"
        position={Position.Left}
        className="!h-3.5 !w-3.5 !border-2 !border-surface !bg-sky-500"
      />

      <div className="border-t border-border/70 px-3 py-1.5">
        {valueHandles.map((handle) => (
          <div
            key={handle}
            className="relative flex h-7 items-center justify-end pr-2 text-[10px] font-medium text-text-muted"
          >
            <span className="max-w-[180px] truncate">
              {handle}
              {fallback === handle ? " (fallback)" : ""}
            </span>
            <Handle
              id={handle}
              type="source"
              position={Position.Right}
              className="!h-3.5 !w-3.5 !border-2 !border-surface !bg-sky-500"
            />
          </div>
        ))}
        {DECISION_EXTRA_BRANCHES.map((handle) => (
          <div
            key={handle}
            className="relative flex h-6 items-center justify-end pr-2 text-[10px] text-text-muted/70"
          >
            <span className="max-w-[180px] truncate italic">{EXTRA_LABELS[handle]}</span>
            <Handle
              id={handle}
              type="source"
              position={Position.Right}
              className="!h-3 !w-3 !border-2 !border-surface !bg-slate-400"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
